// createScoutReportForm.component.jsx


import React, { useEffect, useState } from "react";
import { useDataLayer } from "../../../../Contexts/DataLayer.context";
import { usePlayer } from "../../../../Contexts/playerData.context.jsx";
import { getUserFromToken } from "../../../../utils/functions.ts";
import { getTodayEasternYMD } from "../../../../utils/dateUtils.ts";
import FBXCalendar from "../../../Universals/FBXCalendar/fbxcalendar.component.jsx";
import SelectDropdownContext from "../../../Universals/NewSelectBox/selectBoxContext.component.jsx";
import ClearInputBox from "../../../Universals/newInputBox/newInputBox.component.jsx";

const CreateScoutReportForm = ({
    activePlayer,
    newReport,
    onChange,
    onTogglePrivacy,
    onToday,
    onCancel,
}) => {

    // Report selection in ScoutReportBase is driven by reportData from DataLayer.
    // If we only write localStorage here, the UI won't auto-select the newly created report
    // until a full refresh. So we also update reportData in context.
    const { shortPositionArray, updateReportData } = useDataLayer();
    const API_ENDPOINT = "https://www.fbxchange.com/ReactPHP/scoutreports.php";

    const playerCtx = usePlayer() || {};
    const playerData = playerCtx.playerData;

    const setActivePlayerFn =
        playerCtx.setActivePlayer ||
        playerCtx.updateActivePlayer ||
        playerCtx.updatePlayer ||
        playerCtx.setPlayer;

    const setPlayerDataFn =
        playerCtx.setPlayerData ||
        playerCtx.updatePlayerData ||
        playerCtx.setPlayerArray ||
        playerCtx.updatePlayerArray;



    const fullName = activePlayer
        ? `${(activePlayer.last || "").toUpperCase()}, ${(activePlayer.first || "").toUpperCase()}`
        : "NEW REPORT";

    useEffect(() => {
        // Set today’s date
        onToday();

        // Clear position and exposure
        onChange("COL_POS1", "");
        onChange("exposure", "");
        onChange("JRSEY", "");

        // Reset privacy (false = Not Private; change to true if you want default Private)
        onTogglePrivacy(false);
    }, []);

    const patchReportCountInContext = (FBXID, newCount) => {
        const nextCountStr = String(newCount ?? 0);

        if (Array.isArray(playerData) && typeof setPlayerDataFn === "function") {
            const updated = playerData.map((p) =>
                String(p?.FBXID) === String(FBXID) ? { ...p, report: nextCountStr } : p
            );
            setPlayerDataFn(updated);
        }

        const cur = activePlayer;
        if (cur && String(cur?.FBXID) === String(FBXID) && typeof setActivePlayerFn === "function") {
            setActivePlayerFn({ ...cur, report: nextCountStr });
        }
    };

    const onCreate = async () => {
        const FBXID = activePlayer?.FBXID;
        if (!FBXID) return;

        if (!newReport.COL_POS1) {
            alert("Please select a position.");
            return;
        }

        const TOKEN_KEY = "scoutReportToken";
        const today = getTodayEasternYMD(); // YYYY-MM-DD in Eastern time

        const readTokenArr = () => {
            try {
                const raw = localStorage.getItem(TOKEN_KEY);
                if (!raw) return [];
                const parsed = JSON.parse(raw);

                // support old single-object format
                const arr = Array.isArray(parsed) ? parsed : parsed ? [parsed] : [];
                return arr.filter((x) => x && typeof x === "object");
            } catch (e) {
                console.warn("Failed to parse scoutReportToken:", e);
                return [];
            }
        };

        const upsertToken = (fbxid, reportId) => {
            if (!fbxid || !reportId) return null;

            const tokenArr = readTokenArr();
            const idx = tokenArr.findIndex((r) => String(r?.FBXID) === String(fbxid));

            if (idx !== -1) {
                tokenArr[idx] = { ...tokenArr[idx], FBXID: fbxid, ScoutReport: reportId, ReportDate: today };
            } else {
                tokenArr.push({ FBXID: fbxid, ScoutReport: reportId, ReportDate: today });
            }

            try {
                localStorage.setItem(TOKEN_KEY, JSON.stringify(tokenArr));
            } catch (e) {
                console.error("Failed to save scoutReportToken:", e);
            }

            return tokenArr;
        };

        try {
            const user = await getUserFromToken().catch(() => null);
            if (!user) {
                alert("Could not read user session. Please log in again.");
                return;
            }

            const payload = {
                script: "createNewReport",
                scriptName: "createNewReport", // harmless if backend ignores one of these
                USER_ID: user.scout,
                TEAM: user.team,
                playerID: FBXID,
                displayName: user.displayName,
                exposure: newReport.exposure || "",
                ...newReport,
            };

            const response = await fetch(API_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            const rawText = await response.text().catch(() => "");
            if (!response.ok) {
                console.error("Create report failed:", response.status, rawText);
                alert("Failed to create report. Please try again.");
                return;
            }

            let data;
            try {
                data = JSON.parse(rawText);
            } catch (e) {
                console.error("Create report returned non-JSON:", rawText);
                alert("Create report failed (bad response). Please try again.");
                return;
            }

            if (!data?.success) {
                throw new Error(data?.message || "Failed to create report");
            }
            // ✅ Save token + also push it into DataLayer so ReportList can auto-select immediately
            const tokenArr = await upsertToken(FBXID, data.insertId);
            if (tokenArr && typeof updateReportData === "function") {
                updateReportData(tokenArr);
            }
            console.log("Report created with ID:", data.insertId);
            console.log("Report Count:", data.reportCount);
            // Update report count in context
            patchReportCountInContext(FBXID, data.reportCount);
            onCancel(); // your existing flow: closes + triggers refreshKey in parent
        } catch (error) {
            console.error("Error creating report:", error);
            alert("Failed to create report. Please try again.");
        }
    };


    const canCreate = Boolean(newReport.REPORT_DATE && newReport.COL_POS1);

    const [openCalendar, setOpenCalendar] = useState(null); // Tracks which calendar is open

    const handleToggleCalendar = (fieldName) => {
        if (openCalendar === fieldName) {
            setOpenCalendar(null);
            return;
        }

        setOpenCalendar(fieldName);
    };

    return (
        <div className="container p-0 mb-5">
            <div className="row justify-content-center">
                <div className="col-48 bg-white rounded shadow-sm p-0 border border-1 border-navy">
                    <div className="d-flex gap-2 align-items-center mb-2 alert-secondary ps-3" style={{ lineHeight: "45px", height: '45px' }}>
                        You are creating a new report for:
                        <div className="fw-bold text-primary">
                            {fullName}
                        </div>
                        <div className="fw-bold text-muted">{activePlayer?.colPos1 || "POS"}</div>
                        <div className="fw-bold text-muted">[{activePlayer?.school || "SCHOOL"}]</div>
                        <div className="fw-bold text-muted">College # {activePlayer?.jersey === '-1'
                            ? "0" : activePlayer?.jersey}</div>
                    </div>

                    <div className="p-3">
                        {/* Report Date row */}
                        <div className="row g-2 align-items-center mb-2">
                            <div className="col-8 text-end fw-semibold">
                                Report Date:
                            </div>
                            <div className="col-10">
                                <FBXCalendar
                                    fieldName="REPORT_DATE"
                                    dateIn={newReport.REPORT_DATE}
                                    minYear="1990"
                                    maxYear="2025"
                                    style={{ width: "100%" }}
                                    placeholder="Report Date" // Placeholder for the input
                                    onDateChange={(value) => onChange("REPORT_DATE", value)}
                                    onToggleCalendar={handleToggleCalendar}
                                    isOpen={openCalendar === 'REPORT_DATE'}
                                />
                            </div>
                        </div>
                        <div className="row g-2 align-items-center mb-2">
                            <div className="col-8 text-end fw-semibold">
                                Position:
                            </div>
                            <div className="col-10">
                                <SelectDropdownContext
                                    array={shortPositionArray}              // e.g. [{ code: "1", desc: "1" }, ...]
                                    field="POS_DESC"                        // label field
                                    fieldValue="POS_SPEC_CODE"              // value field
                                    label="Select Position"                 // placeholder in box
                                    value={newReport.COL_POS1}              // current value
                                    onChange={(e) => onChange("COL_POS1", e.target.value)}
                                />

                            </div>
                            <div className="text-danger text-start fs-8 col-12">*Required</div>
                        </div>
                        <div className="row g-2 align-items-center mb-2">
                            <div className="col-8 text-end fw-semibold">
                                Jersey Number:
                            </div>
                            <div className="col-10">
                                <ClearInputBox
                                    value={newReport.JRSEY}
                                    placeholder="Jersey (Use -1 for 0)"
                                    onChange={(e) => onChange("JRSEY", e.target.value)}
                                />
                            </div>
                            <div className="text-success text-start fs-8 col-12">Not Required - But Recommended</div>
                        </div>
                        {/* Exposure row */}
                        <div className="row g-2 align-items-center mb-2">
                            <div className="col-8 text-end fw-semibold">
                                Exposure:
                            </div>
                            <div className="col-10">
                                <ClearInputBox
                                    value={newReport.exposure}
                                    placeholder="Exposure (e.g. 'High', 'Medium', 'Low')"
                                    onChange={(e) => onChange("exposure", e.target.value)}
                                />
                            </div>
                            <div className="text-success text-start fs-8 col-12">
                                Not Required – But Recommended
                            </div>
                        </div>


                        {/* Privacy row */}
                        <div className="row g-2 align-items-center mb-2">
                            <div className="col-8 text-end fw-semibold">
                                Privacy:
                            </div>
                            <div className="col-10">
                                <input
                                    type="text"
                                    readOnly
                                    className={
                                        `form-control form-control-sm ` +
                                        (newReport.isPrivate ? "bg-purple text-white" : "")
                                    }
                                    value={
                                        newReport.isPrivate
                                            ? "This report will be Private"
                                            : "Currently: Not Private"
                                    }
                                />

                            </div>
                            <div className="col-24 col-md-18 d-flex gap-2">
                                <button
                                    type="button"
                                    className={`btn btn-sm w-50 ${newReport.isPrivate
                                        ? "btn-primary"
                                        : "btn-outline-primary"
                                        }`}
                                    onClick={() => onTogglePrivacy(true)}
                                >
                                    Private
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-sm w-50 ${!newReport.isPrivate
                                        ? "btn-primary"
                                        : "btn-outline-primary"
                                        }`}
                                    onClick={() => onTogglePrivacy(false)}
                                >
                                    Not Private
                                </button>
                            </div>
                        </div>

                        {/* Create / Cancel row */}
                        <div className="row g-2 align-items-center mb-2">
                            <div className="col-8 text-end fw-semibold">
                                Create:
                            </div>
                            <div className="col-10">
                                <button
                                    type="button"
                                    className="btn btn-outline-danger w-100"
                                    onClick={onCancel}
                                >
                                    Cancel
                                </button>
                            </div>
                            <div className="col-24 col-md-18">
                                {canCreate ? (
                                    <button
                                        type="button"
                                        className="btn btn-outline-success w-100"
                                        onClick={onCreate}
                                    >
                                        Create
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary w-100"
                                        disabled
                                    >
                                        Select Date &amp; Position to Create
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div >
    );
};

export default CreateScoutReportForm;
